import { Box, FormControl, InputLabel, Select, MenuItem, TextField, Button, Typography, Slider, Stack } from "@mui/material";
import Webcam from "react-webcam";

export default function ScanInput({
  accounts,
  selectedAccount,
  setSelectedAccount,
  canScanCancelPackets = false,
  cancelModeValue = "CANCEL",
  scannedTrackingId,
  setScannedTrackingId,
  toggleCamera,
  showCamera,
  webcamRef,
  triggerAutoFocus,
  onCameraReady,
  cameraStatus = "",
  cameraCapabilities = {},
  cameraZoom = 1,
  setCameraZoom,
  torchOn = false,
  setTorchOn,
  lastSuccessScan,
  inputRef,
  onSubmit,
  cooldownActive,
}) {
  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#f4f4f4",
        padding: 2,
        borderRadius: 2,
        boxShadow: 2,
        marginBottom: "20px",
        opacity: cooldownActive ? 0.6 : 1,
        transition: "opacity 0.3s",
      }}
    >
      {/* Account Dropdown */}
      <FormControl fullWidth sx={{ marginBottom: 2 }}>
        <InputLabel>Select Account</InputLabel>
        <Select
          value={selectedAccount}
          onChange={(e) => setSelectedAccount(e.target.value)}
          label="Select Account"
        >
          <MenuItem value="">-- Select Account --</MenuItem>
          <MenuItem value="ALL">ALL</MenuItem>
          {canScanCancelPackets && (
            <MenuItem value={cancelModeValue}>CANCEL - Scan Cancel Packet</MenuItem>
          )}
          {accounts.map((a) => (
            <MenuItem key={a.id} value={a.name}>
              {a.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Tracking Input + Camera */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <TextField
          inputRef={inputRef}
          autoFocus
          label={cooldownActive ? "⏳ Cooldown..." : "Scanned Tracking ID"}
          value={scannedTrackingId}
          onChange={(e) => setScannedTrackingId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (!scannedTrackingId.trim()) return;
              onSubmit();
            }
          }}
          disabled={cooldownActive}
          fullWidth
        />
        <Button
          onClick={toggleCamera}
          variant="contained"
          color={showCamera ? "warning" : "primary"}
          sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}
        >
          📷
        </Button>
      </Box>

      {/* Camera */}
      {showCamera && (
        <Box
          sx={{
            marginTop: 2,
            width: "100%",
            height: { xs: 150, sm: 300, md: 400 },
            position: "relative",
            borderRadius: 2,
            overflow: "hidden",
          }}
          onClick={triggerAutoFocus}
        >
          <Webcam
            audio={false}
            ref={webcamRef}
            onUserMedia={onCameraReady}
            screenshotFormat="image/jpeg"
            videoConstraints={{
              facingMode: { ideal: "environment" },
              width: { ideal: 2560 },
              height: { ideal: 1440 },
              frameRate: { ideal: 30, max: 60 },
            }}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />

          {/* Guide box */}
          <Box
            sx={{
              position: "absolute",
              top: "25%",
              left: "8%",
              width: "84%",
              height: "38%",
              border: "3px solid #00e676",
              borderRadius: 2,
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.25)",
              pointerEvents: "none",
            }}
          />

          {cameraStatus && (
            <Box
              sx={{
                position: "absolute",
                left: 8,
                right: 8,
                bottom: 8,
                bgcolor: "rgba(0,0,0,0.72)",
                color: "#fff",
                px: 1,
                py: 0.75,
                borderRadius: 1,
                textAlign: "center",
                fontSize: 13,
                fontWeight: "bold",
              }}
            >
              {cameraStatus}
            </Box>
          )}
        </Box>
      )}

      {showCamera && (
        <Stack spacing={1.25} sx={{ mt: 1.5 }}>
          {cameraCapabilities.zoom && (
            <Box sx={{ px: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                Zoom: {Number(cameraZoom || 1).toFixed(1)}x
              </Typography>
              <Slider
                size="small"
                value={cameraZoom}
                min={cameraCapabilities.zoom.min}
                max={Math.min(cameraCapabilities.zoom.max, 6)}
                step={cameraCapabilities.zoom.step || 0.1}
                onChange={(_event, next) => setCameraZoom?.(next)}
                valueLabelDisplay="auto"
              />
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {cameraCapabilities.torch && (
              <Button
                size="small"
                variant={torchOn ? "contained" : "outlined"}
                onClick={() => setTorchOn?.(!torchOn)}
              >
                {torchOn ? "Torch On" : "Torch"}
              </Button>
            )}
            <Button size="small" variant="outlined" onClick={triggerAutoFocus}>
              Focus
            </Button>
          </Box>
        </Stack>
      )}

      {/* Last Scan */}
      {lastSuccessScan.id && (
        <Typography sx={{ marginTop: 1, fontWeight: "bold" }}>
          ✅ Last Scan: {lastSuccessScan.id} ({lastSuccessScan.account})
        </Typography>
      )}
    </Box>
  );
}
